import { useState, useEffect, useRef } from 'react';
import { X, Send, Maximize2, Minimize2 } from 'lucide-react';
import axios from 'axios';
import { CognitiveBias } from '../../types';
import { createPortal } from 'react-dom';
import brainyMascot from '../../assets/mascot/brainy_with_a_spark.png';
import brainyWaving from '../../assets/mascot/brainy_waving.png';
import { MarkdownContent } from '../../components/ui/MarkdownContent';

interface BiasChatButtonProps {
  bias: CognitiveBias;
}

interface Message {
  isUser: boolean;
  content: string;
}

const BiasChatButton: React.FC<BiasChatButtonProps> = ({ bias }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const portalContainerRef = useRef<HTMLDivElement | null>(null);

  // Create portal container on mount
  useEffect(() => {
    portalContainerRef.current = document.createElement('div');
    portalContainerRef.current.setAttribute('data-brainy-chat', '');
    document.body.appendChild(portalContainerRef.current);

    return () => {
      // Clean up portal container on unmount
      if (portalContainerRef.current) {
        document.body.removeChild(portalContainerRef.current);
        portalContainerRef.current = null;
      }
    };
  }, []);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Check on mount
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setIsFullScreen(false); // Reset fullscreen when opening
    }
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!inputValue.trim()) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message to chat
    setMessages(prev => [...prev, { isUser: true, content: userMessage }]);
    
    // Add loading indicator
    setMessages(prev => [...prev, { isUser: false, content: '...' }]);
    setIsLoading(true);
    
    try {
      // Use the production API URL when in production
      const productionURL = 'https://www.cognitivebiaslab.com/api';
      const devURL = 'http://localhost:3001/api';
      const API_BASE_URL = 
        window.location.hostname === 'localhost' ? 
        (import.meta.env.VITE_API_URL || devURL) : 
        productionURL;
      
      // Send biasId (derived from prop) and the message
      // The backend now handles fetching bias details
      const response = await axios.post(`${API_BASE_URL}/gemini/coach/chat`, {
        biasId: bias.id, // Send only the ID
        message: userMessage,
        conversation: messages.filter(m => m.content !== '...') // Send previous messages if needed by backend context
      });
      
      // Remove loading indicator
      setMessages(prev => prev.filter(m => m.content !== '...'));
      
      // Add AI response
      setMessages(prev => [...prev, { isUser: false, content: response.data.response }]);
      
      // No need to track hasInitialized here anymore as biasId is always sent

    } catch (error) {
      console.error('Error sending message:', error);
      
      // Remove loading indicator
      setMessages(prev => prev.filter(m => m.content !== '...'));
      
      // Add error message
      setMessages(prev => [...prev, { 
        isUser: false, 
        content: 'Sorry, there was an error processing your request. Please try again.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Create the button and chat window elements to be rendered via portal
  const chatElements = (
    <>
      {/* Overlay for desktop expanded mode */}
      {isOpen && isFullScreen && !isMobile && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 9998,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
          onClick={(e) => {
            // Close fullscreen when clicking outside the chat window
            if (e.target === e.currentTarget) {
              setIsFullScreen(false);
            }
          }}
        />
      )}

      {/* Floating button with Brainy mascot */}
      <div style={{
        position: 'fixed',
        bottom: '32px',
        right: '32px',
        zIndex: 9999,
        pointerEvents: 'auto',
        display: isOpen && isFullScreen ? 'none' : 'block'
      }}>
        <div 
          className="p-[3px] rounded-full bg-gradient-to-r from-orange-500 to-pink-500 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
          style={{ width: '86px', height: '86px' }}
        >
          <button
            onClick={toggleChat}
            className="w-full h-full rounded-full bg-white focus:outline-none transition-all duration-300 hover:scale-[1.02] overflow-hidden"
            aria-label="Chat with Brainy, your Cognitive Bias Coach"
          >
            {isOpen ? (
              <div className="h-full w-full flex items-center justify-center bg-gradient-to-r from-orange-500 to-pink-500">
                <X className="w-6 h-6 text-white" />
              </div>
            ) : (
              <img 
                src={brainyMascot} 
                alt="Brainy, the Cognitive Bias Lab mascot" 
                className="h-full w-full object-cover"
              />
            )}
          </button>
        </div>
      </div>

      {/* Chat window */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          zIndex: 9999,
          pointerEvents: 'auto',
          ...(isFullScreen ? (
            isMobile ? 
            // Mobile fullscreen - take the entire screen with safe area insets
            {
              top: 'env(safe-area-inset-top)',
              left: 'env(safe-area-inset-left)',
              right: 'env(safe-area-inset-right)',
              bottom: 'env(safe-area-inset-bottom)',
              width: '100%',
              height: '100%',
              padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)'
            } : 
            // Desktop fullscreen - centered large popup
            {
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80%',
              maxWidth: '900px',
              height: '80%',
              maxHeight: '700px'
            }
          ) : {
            // Regular mode - adjusted for better mobile fit
            bottom: isMobile ? '16px' : '108px',
            right: isMobile ? '16px' : '32px',
            width: isMobile ? 'calc(100% - 32px)' : '24rem',
            height: isMobile ? 'calc(100% - 32px)' : '500px',
            maxHeight: isMobile ? '80vh' : '500px'
          })
        }}>
          <div className="h-full bg-white rounded-2xl shadow-2xl flex flex-col border border-gray-200 overflow-hidden"
               style={{ borderRadius: (isFullScreen && isMobile) ? '0' : '1rem' }}>
            {/* Chat header */}
            <div className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-4 py-3 flex items-center justify-between">
              <h3 className="font-semibold">Brainy - Your cognitive bias companion</h3>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={toggleFullScreen} 
                  className="text-white hover:bg-white/20 p-1 rounded-full"
                  aria-label={isFullScreen ? "Exit full screen" : "Full screen"}
                >
                  {isFullScreen ? (
                    <Minimize2 className="w-5 h-5" />
                  ) : (
                    <Maximize2 className="w-5 h-5" />
                  )}
                </button>
                <button 
                  onClick={toggleChat} 
                  className="text-white hover:bg-white/20 p-1 rounded-full"
                  aria-label="Close chat"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Chat messages */}
            <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-gray-500 p-6">
                  <img 
                    src={brainyWaving} 
                    alt="Brainy waving hello" 
                    className="w-32 h-32 mb-4"
                  />
                  <p className="mb-2">Hi, I'm Brainy! Ask me anything about {bias.name}!</p>
                  <p className="text-sm">I can explain the bias, provide examples, or suggest strategies to overcome it.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <div 
                      key={index} 
                      className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
                    >
                      {!message.isUser && message.content !== '...' && (
                        <img 
                          src={brainyMascot} 
                          alt="Brainy" 
                          className="w-12 h-12 mr-2 self-end"
                        />
                      )}
                      <div 
                        className={`max-w-3/4 p-3 rounded-lg ${
                          message.isUser 
                            ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-br-none' 
                            : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none'
                        }`}
                      >
                        {message.content === '...' ? (
                          <div className="flex items-center space-x-1">
                            <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                          </div>
                        ) : (
                          message.isUser ? (
                            message.content.split('\n').map((paragraph, idx) => (
                              <p key={idx} className={idx > 0 ? 'mt-2' : ''}>
                                {paragraph}
                              </p>
                            ))
                          ) : (
                            <div className="prose prose-sm max-w-none dark:prose-invert">
                              <MarkdownContent content={message.content} />
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Chat input */}
            <form onSubmit={sendMessage} className="p-3 bg-white border-t border-gray-200 flex items-center gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                disabled={isLoading}
                placeholder="Ask Brainy a question..."
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                className={`p-2 rounded-full ${
                  !inputValue.trim() || isLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:shadow-md'
                }`}
                aria-label="Send message"
              >
                <Send className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );

  // Render via Portal to ensure it's at the document root level
  return portalContainerRef.current ? createPortal(
    chatElements,
    portalContainerRef.current
  ) : null;
};

export default BiasChatButton; 